import type { OrderItem } from '@repo/db/schema/orders'

// The flag's NAME and its predicate live with the sweep that has to refuse to
// run on it (E3), not with the screen that raises it — one string, read in one
// place.
import { hasBadMaterial } from './acceptance.ts'

/**
 * Source material: the link, the declaration, and what happens when the link is
 * wrong (spec §7 "Odpowiedzialność za materiał" · G4).
 *
 * Montage is the one product where the customer supplies the raw input, and the
 * spec resolves the obvious failure mode without ambiguity: a bad link is the
 * client's problem, they have no claim over the delay, and the editor is not
 * left holding a deadline they cannot meet. Three mechanisms carry that:
 *
 *   THE LINK          Required to order, never uploaded. A montage job is tens
 *                     of gigabytes; storing them would make this a storage
 *                     business with an editing side-line.
 *   THE DECLARATION   A compulsory tick that the link and the material are
 *                     right. It is what the "brak roszczeń" rule rests on, so
 *                     it is stored as an INSTANT — a claim with no date behind
 *                     it is evidence of nothing.
 *   THE FLAG          The editor says there is nothing to work with. It stops
 *                     the auto-acceptance sweep (E3, already), hands the case
 *                     to Support, and starts a 90-day clock.
 *
 * Pure predicates over a row and an instant, like `acceptance.ts` beside it.
 * The use-cases that WRITE any of this are in `material.service.ts`, split for
 * the same reason acceptance is: this half has to be importable by the checkout
 * mapper, which the status writer imports in turn.
 */

const DAY = 24 * 60 * 60 * 1000

/** Spec §7: "Support dopasowuje inny termin w oknie do 90 dni." */
export const MATERIAL_WINDOW_DAYS = 90

/**
 * When the client is reminded that the window is closing.
 *
 * A week out, because replacing a link is a five-minute job somebody has to
 * remember to do — and a reminder on the last day is a reminder about a
 * decision that has already been made for them.
 */
export const MATERIAL_REMINDER_DAYS = MATERIAL_WINDOW_DAYS - 7

/** The subset of a line these rules read. */
export type MaterialState = Pick<OrderItem, 'status' | 'badMaterialAt' | 'flags'>

/** When the client runs out of time to supply a working link. */
export function materialDeadline(badMaterialAt: Date): Date {
  return new Date(badMaterialAt.getTime() + MATERIAL_WINDOW_DAYS * DAY)
}

/** When the reminder about it should go out. */
export function materialReminderAt(badMaterialAt: Date): Date {
  return new Date(badMaterialAt.getTime() + MATERIAL_REMINDER_DAYS * DAY)
}

/**
 * Is this link something the editor can actually open?
 *
 * Deliberately shallow. It refuses what is definitely not a link — an empty
 * box, a filename, `javascript:` — and accepts everything else, because the
 * real check is a person clicking it, and a clever pattern that rejected a
 * valid share URL from a service nobody here has heard of would be worse than
 * no check at all. What it MUST catch is the scheme: this string ends up in an
 * `href` on a panel screen, and `javascript:` there is a script somebody else
 * wrote running in a staff session.
 */
export function isSourceUrl(value: string): boolean {
  const trimmed = value.trim()
  if (trimmed.length < 8 || trimmed.length > 2000) return false
  try {
    const url = new URL(trimmed)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * Whether this line has already had its "seven days left" nudge.
 *
 * A flag rather than a column, for the same reason `acceptanceRemindersSent` is
 * one: the sweep sees every flagged line on every pass, and without somewhere
 * to record that it has spoken it would speak every night for a week.
 */
export const MATERIAL_REMINDED_FLAG = 'materialReminded'

/**
 * The editor says there is more tape than the client declared (spec §8 · G7).
 *
 * A flag rather than a number, because the number is not knowable from here:
 * spec §8 routes it to a person — "Support dolicza brakującą dopłatę przed
 * startem pracy" — and what the system owes is the interruption, not the
 * arithmetic. The missing money is raised as an `EXTRA_FOOTAGE` surcharge by
 * whoever measures it.
 *
 * Deliberately NOT `badMaterial`: the material is fine, there is just more of
 * it than was paid for. Sharing the flag would halt auto-acceptance on an order
 * whose only problem is a bill.
 */
export const FOOTAGE_UNDERDECLARED_FLAG = 'footageUnderdeclared'

export function isFootageUnderdeclared(state: Pick<OrderItem, 'flags'>): boolean {
  return state.flags?.[FOOTAGE_UNDERDECLARED_FLAG] === true
}

/**
 * Should the client be reminded that their 90 days are running out?
 *
 * `false` for a line nobody is waiting on material for, for one that has
 * already been told, and for one whose window has closed — by then the decision
 * has been made for them, and a reminder about it is the most annoying message
 * in the product.
 */
export function shouldRemindAboutMaterial(state: MaterialState, now: Date): boolean {
  if (!state.badMaterialAt || !hasBadMaterial(state)) return false
  if (state.flags?.[MATERIAL_REMINDED_FLAG] === true) return false
  if (now.getTime() >= materialDeadline(state.badMaterialAt).getTime()) return false
  return now.getTime() >= materialReminderAt(state.badMaterialAt).getTime()
}

/**
 * Should the line be closed because no working material ever arrived?
 *
 * Spec §7: "Jeśli klient nie dostarczy poprawnego materiału w 90 dni: Support
 * przypomina, potem zamyka." Closed as `CANCELLED`, because nothing was
 * delivered — and any partial refund is explicitly an Admin decision, which is
 * why nothing here writes one.
 */
export function shouldCloseForMaterial(state: MaterialState, now: Date): boolean {
  if (!state.badMaterialAt || !hasBadMaterial(state)) return false
  if (state.status === 'COMPLETED' || state.status === 'CANCELLED') return false
  return now.getTime() >= materialDeadline(state.badMaterialAt).getTime()
}
